import Layout from "@/layouts/layout";
import PageHeading from "@/components/layout/PageHeading";
import Panel from "@/components/ui/Panel";
import Button, { ButtonLink, ButtonAnchor } from "@/components/ui/Button";
import Badge from "@/components/ui/Badge";
import EmptyState from "@/components/ui/EmptyState";
import { Checkbox, Input, Textarea } from "@/components/ui/Field";
import EventStatusBadge from "@/components/events/EventStatusBadge";
import { router } from "@inertiajs/react";
import { useState } from "react";
import EventMap from "@/components/admin/EventMap";
import type {
  GroupSuggestion,
  MapPin,
  SatelliteEvent,
  SlackHealth,
} from "@/interfaces/satellite_event";
import type { CustomPageRow } from "@/interfaces/event_page";

// An application carries the flags EventApplicationReview computed over the
// whole queue. Signals for ranking what to open first - never a verdict, and
// they reject nothing.
type Application = SatelliteEvent & { flags?: string[] };

type ApplicationGroup = {
  key: string;
  label: string | null;
  contested: boolean;
  applications: Application[];
};

// One application inside a group. The decision is "is this the person", so the
// answers HQ weighs sit next to the buttons rather than a click away.
function ApplicationCard({
  event,
  contested,
  selected,
  onToggle,
  flagLabels,
}: {
  event: Application;
  contested: boolean;
  selected: boolean;
  onToggle: (id: number) => void;
  flagLabels: Record<string, string>;
}) {
  const [notes, setNotes] = useState("");
  const flags = event.flags ?? [];

  return (
    <div className="border-line bg-surface border-2 p-4">
      <div className="flex flex-wrap items-center gap-2">
        <Checkbox
          checked={selected}
          onChange={() => onToggle(event.id)}
          aria-label={`Select ${event.organizer_name}'s application`}
        />
        <span className="font-display text-body text-ink font-bold">
          {event.organizer_name}
        </span>
        <Badge tone="neutral">{event.location_label}</Badge>
        <Badge tone="info">~{event.expected_attendees ?? "?"} expected</Badge>
        <span className="text-caption text-ink-soft">
          submitted {event.submitted_at?.slice(0, 10) ?? "-"}
        </span>
      </div>

      {/* Sat above the answers rather than beside the buttons: these are a
          reason to read the application more carefully, not a reason to press
          a different button. */}
      {flags.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {flags.map((flag) => (
            <Badge key={flag} tone="warning">
              {flagLabels[flag] ?? flag}
            </Badge>
          ))}
          <span className="text-caption text-ink-soft">
            worth a closer read - none of this rejects anything
          </span>
        </div>
      )}

      {event.pitch && (
        <div className="mt-3">
          <p className="font-display text-caption text-ink font-bold">
            Why they want to run one
          </p>
          <p className="text-body text-ink-muted">{event.pitch}</p>
        </div>
      )}

      {event.experience && (
        <div className="mt-3">
          <p className="font-display text-caption text-ink font-bold">
            Experience
          </p>
          <p className="text-body text-ink-muted">{event.experience}</p>
        </div>
      )}

      <div className="mt-4 flex flex-col gap-3">
        <Textarea
          rows={2}
          placeholder="Notes for them (required to send back, optional otherwise)"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />

        <div className="flex flex-wrap items-center gap-3">
          <Button
            onClick={() =>
              router.post(`/admin/events/${event.id}/select_poc`, {
                review_notes: notes,
              })
            }
          >
            {contested ? "Pick as POC" : "Approve"}
          </Button>
          <Button
            variant="secondary"
            disabled={notes.trim().length === 0}
            onClick={() =>
              router.post(`/admin/events/${event.id}/reject`, {
                review_notes: notes,
              })
            }
          >
            Send back
          </Button>

          <EventActions event={event} />
        </div>

        {contested && (
          <p className="text-caption text-ink-soft">
            Picking them approves this one and lets the others in this group
            down gently, pointed at their event.
          </p>
        )}
      </div>
    </div>
  );
}

function GroupPanel({
  group,
  selected,
  onToggle,
  onToggleAll,
  flagLabels,
}: {
  group: ApplicationGroup;
  selected: Set<number>;
  onToggle: (id: number) => void;
  onToggleAll: (ids: number[], select: boolean) => void;
  flagLabels: Record<string, string>;
}) {
  const count = group.applications.length;
  const ids = group.applications.map((e) => e.id);
  const allSelected = ids.every((id) => selected.has(id));

  return (
    <Panel
      title={`${group.key} - ${count} ${count === 1 ? "application" : "applications"}`}
      tone={group.contested ? "alt" : undefined}
    >
      <div className="flex flex-col gap-4">
        <label className="text-caption text-ink-soft flex w-fit cursor-pointer items-center gap-2">
          <Checkbox
            checked={allSelected}
            onChange={() => onToggleAll(ids, !allSelected)}
          />
          Select all {count} in {group.key}
        </label>

        {group.contested && (
          <p className="text-body text-ink-muted">
            {count} people want to run this one. Pick one as the point of
            contact - or select them and give them different group names to run
            them as separate events.
          </p>
        )}

        {group.applications.map((event) => (
          <ApplicationCard
            key={event.id}
            event={event}
            contested={group.contested}
            selected={selected.has(event.id)}
            onToggle={onToggle}
            flagLabels={flagLabels}
          />
        ))}
      </div>
    </Panel>
  );
}

// One label applied to everything ticked. The alternative -- a box per card --
// meant typing "Silicon Valley" twenty times and twenty page reloads to merge
// one metro.
function BulkGroupBar({
  count,
  labels,
  onApply,
  onClear,
}: {
  count: number;
  labels: string[];
  onApply: (label: string) => void;
  onClear: () => void;
}) {
  const [label, setLabel] = useState("");

  return (
    <div className="border-line bg-surface sticky bottom-4 z-10 flex flex-wrap items-center gap-3 border-2 p-3 shadow-[0_4px_0_var(--color-line)]">
      <span className="font-display text-body text-ink font-bold">
        {count} selected
      </span>

      <Input
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        placeholder="Group them as… (e.g. Silicon Valley)"
        list="existing-group-labels"
        className="max-w-64 flex-1"
      />
      {/* Existing labels as suggestions, so merging into a group that already
          exists doesn't depend on spelling it identically. */}
      <datalist id="existing-group-labels">
        {labels.map((l) => (
          <option key={l} value={l} />
        ))}
      </datalist>

      <Button
        size="sm"
        disabled={label.trim().length === 0}
        onClick={() => onApply(label.trim())}
      >
        Group
      </Button>
      <Button size="sm" variant="secondary" onClick={() => onApply("")}>
        Ungroup
      </Button>
      <Button size="sm" variant="ghost" onClick={onClear}>
        Clear
      </Button>
    </div>
  );
}

// Editing reuses the organizer dashboard rather than a second admin-only form:
// EventPolicy#update? already lets HQ in, and one form means the two can't
// drift apart.
function EventActions({ event }: { event: SatelliteEvent }) {
  function remove() {
    const going = event.rsvp_count ?? 0;
    const warning =
      going > 0
        ? `Delete ${event.name}? This also removes ${going} ${going === 1 ? "RSVP" : "RSVPs"} - those people lose their spot.`
        : `Delete ${event.name}?`;

    if (confirm(warning)) router.delete(`/admin/events/${event.id}`);
  }

  return (
    <div className="flex items-center gap-2">
      <ButtonLink href={`/organize/${event.id}`} size="sm" variant="secondary">
        Edit
      </ButtonLink>
      {event.page_published && (
        <Button
          size="sm"
          variant="ghost"
          onClick={() =>
            router.post(`/admin/events/${event.id}/unpublish_page`)
          }
        >
          Take page down
        </Button>
      )}
      {/* For the organizer who never answered, or asked in Slack instead. */}
      {event.aasm_state === "approved" && !event.postponed && (
        <Button
          size="sm"
          variant="ghost"
          onClick={() => {
            const going = event.rsvp_count ?? 0;
            if (
              confirm(
                `Move ${event.name} to October? This clears its date and emails the organizer${
                  going > 0
                    ? `, who is asked to tell the ${going} ${going === 1 ? "person" : "people"} on their roster. We don't email them.`
                    : "."
                }`,
              )
            )
              router.post(`/admin/events/${event.id}/postpone`);
          }}
        >
          Move to Oct
        </Button>
      )}
      <Button size="sm" variant="ghost" onClick={remove}>
        Delete
      </Button>
    </div>
  );
}

// Approved events with no channel. This used to be invisible: the job that
// creates a channel was dropped silently, so the only signal was an organizer
// eventually saying they never got one.
function SlackHealthPanel({ health }: { health: SlackHealth }) {
  if (health.missing === 0) return null;

  return (
    <Panel
      title={`slack-channels.log - ${health.missing} missing`}
      tone="alt"
      actions={
        <Button
          size="sm"
          onClick={() => router.post("/admin/events/backfill_slack")}
        >
          Create all {health.missing}
        </Button>
      }
    >
      <div className="flex flex-col gap-3">
        <p className="text-body text-ink-muted">
          {health.missing} of {health.approved} approved{" "}
          {health.missing === 1 ? "event has" : "events have"} no Slack channel
          {health.failed > 0 && ` - ${health.failed} recorded a reason`}. Setup
          is idempotent, so re-running only fills in what's missing.
        </p>

        {health.events.map((event) => (
          <div
            key={event.id}
            className="border-line bg-surface flex flex-wrap items-center gap-3 border-2 p-3"
          >
            <div className="min-w-0 flex-1">
              <p className="text-body text-ink truncate font-bold">
                {event.name}
              </p>
              <p className="text-caption text-ink-soft truncate">
                {event.location_label}
                {event.approved_at &&
                  ` · approved ${event.approved_at.slice(0, 10)}`}
              </p>
              {event.error && (
                <p className="text-caption text-danger font-literal mt-1">
                  {event.error}
                </p>
              )}
            </div>
            <Badge tone={event.error ? "danger" : "warning"}>
              {event.error ? "failed" : "never ran"}
            </Badge>
            <Button
              size="sm"
              variant="secondary"
              onClick={() =>
                router.post(`/admin/events/${event.id}/retry_slack`)
              }
            >
              Retry
            </Button>
          </div>
        ))}
      </div>
    </Panel>
  );
}

// Distance knows what a string comparison can't: that these applications are
// one metro. Advisory only - applying it is what writes group_label.
// The raw HTML/CSS organizers wrote. Blocks-only pages aren't here: markdown is
// sanitized on the way in and there would be one row per event. This is the list
// HQ actually has to look at.
function CustomPagesPanel({ pages }: { pages: CustomPageRow[] }) {
  if (pages.length === 0) return null;

  return (
    <Panel title={`custom-code.csv - ${pages.length}`} tone="alt">
      <div className="flex flex-col gap-1">
        <p className="text-caption text-ink-soft mb-2">
          Written by organizers, rendered in a sandbox that blocks JavaScript.
          Taking a page down keeps what they wrote; clearing the code doesn't.
        </p>

        {pages.map((page) => (
          <div
            key={page.id}
            className="border-line/20 flex flex-wrap items-center gap-3 border-b py-2.5 last:border-b-0"
          >
            <div className="min-w-0 flex-1">
              <p className="text-body text-ink truncate font-bold">
                {page.name}
              </p>
              <p className="text-caption text-ink-soft truncate">
                {page.location_label} · by {page.organizer_name} ·{" "}
                {page.bytes.toLocaleString()} bytes
              </p>
            </div>

            <Badge tone={page.page_published ? "warning" : "neutral"}>
              {page.page_published ? "live" : "not published"}
            </Badge>

            <ButtonAnchor
              href={`/events/${page.id}`}
              target="_blank"
              rel="noreferrer"
              size="sm"
              variant="secondary"
            >
              Look at it
            </ButtonAnchor>

            {page.page_published && (
              <Button
                size="sm"
                variant="secondary"
                onClick={() =>
                  router.post(`/admin/events/${page.id}/unpublish_page`)
                }
              >
                Take down
              </Button>
            )}

            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                if (
                  confirm(
                    `Delete the custom code on ${page.name}? They can't get it back.`,
                  )
                ) {
                  router.delete(`/admin/events/${page.id}/custom_html`);
                }
              }}
            >
              Clear code
            </Button>
          </div>
        ))}
      </div>
    </Panel>
  );
}

function SuggestionPanel({
  suggestions,
  onApply,
}: {
  suggestions: GroupSuggestion[];
  onApply: (ids: number[], label: string) => void;
}) {
  if (suggestions.length === 0) return null;

  return (
    <Panel title={`suggested-groups.txt - ${suggestions.length}`} tone="alt">
      <div className="flex flex-col gap-3">
        {suggestions.map((suggestion) => (
          <div
            key={suggestion.key}
            className="border-line bg-surface flex flex-wrap items-center gap-3 border-2 p-3"
          >
            <div className="min-w-0 flex-1">
              <p className="text-body text-ink font-bold">
                {suggestion.event_ids.length} applications look like one metro
              </p>
              <p className="text-caption text-ink-soft">
                {suggestion.places.join(" · ")}
                {suggestion.widest_gap_km !== null &&
                  ` - widest gap ${suggestion.widest_gap_km} km`}
              </p>
            </div>
            {suggestion.label && (
              <Button
                size="sm"
                onClick={() => onApply(suggestion.event_ids, suggestion.label!)}
              >
                Group as “{suggestion.label}”
              </Button>
            )}
          </div>
        ))}
      </div>
    </Panel>
  );
}

const SLACK_TONE = {
  pending: { tone: "warning" as const, label: "no channel" },
  failed: { tone: "danger" as const, label: "channel failed" },
  warning: { tone: "warning" as const, label: "invites failed" },
};

function EventRow({ event }: { event: SatelliteEvent }) {
  return (
    <div className="border-line/20 flex flex-wrap items-center gap-3 border-b py-2.5 last:border-b-0">
      <div className="min-w-0 flex-1">
        <p className="text-body text-ink truncate font-bold">{event.name}</p>
        <p className="text-caption text-ink-soft truncate">
          {event.location_label} · {event.starts_at_short_label ?? "Date TBD"} ·
          by {event.organizer_name}
        </p>
      </div>
      {/* An urgent message outranks anything else on the row. */}
      {event.has_urgent && <Badge tone="danger">urgent</Badge>}
      {/* The two states the October move leaves behind, both chase-worthy. */}
      {event.awaiting_new_date && <Badge tone="danger">no date</Badge>}
      {event.reschedule_pending && <Badge tone="warning">undecided</Badge>}
      {event.funding_held && <Badge tone="warning">grant held</Badge>}
      {(event.unread_for_hq ?? 0) > 0 && (
        <Badge tone="warning">{event.unread_for_hq} new</Badge>
      )}
      <Badge tone="neutral">{event.rsvp_count ?? 0} RSVPs</Badge>
      {event.slack_channel_url ? (
        <a
          href={event.slack_channel_url}
          target="_blank"
          rel="noreferrer"
          className="text-caption text-link font-literal font-bold hover:underline"
        >
          #{event.slack_channel_name}
        </a>
      ) : (
        event.slack_channel_status &&
        event.slack_channel_status in SLACK_TONE && (
          <Badge
            tone={
              SLACK_TONE[event.slack_channel_status as keyof typeof SLACK_TONE]
                .tone
            }
          >
            {
              SLACK_TONE[event.slack_channel_status as keyof typeof SLACK_TONE]
                .label
            }
          </Badge>
        )
      )}
      <EventStatusBadge state={event.aasm_state} />
      <EventActions event={event} />
    </div>
  );
}

export default function AdminEvents({
  groups,
  application_flag_labels,
  suggestions,
  map_pins,
  slack_health,
  custom_pages,
  approved,
  rejected,
  not_selected,
}: {
  groups: ApplicationGroup[];
  application_flag_labels: Record<string, string>;
  suggestions: GroupSuggestion[];
  map_pins: MapPin[];
  slack_health: SlackHealth;
  custom_pages: CustomPageRow[];
  approved: SatelliteEvent[];
  rejected: SatelliteEvent[];
  not_selected: SatelliteEvent[];
}) {
  const waiting = groups.reduce((n, g) => n + g.applications.length, 0);
  const contested = groups.filter((g) => g.contested).length;

  const [selected, setSelected] = useState<Set<number>>(new Set());
  const existingLabels = [
    ...new Set(groups.map((g) => g.label).filter((l): l is string => !!l)),
  ].sort();

  function toggle(id: number) {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function toggleAll(ids: number[], select: boolean) {
    setSelected((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => (select ? next.add(id) : next.delete(id)));
      return next;
    });
  }

  function applyGroup(label: string) {
    applyGroupTo([...selected], label);
  }

  function applyGroupTo(ids: number[], label: string) {
    router.patch(
      "/admin/events/group",
      { ids, group_label: label },
      { onSuccess: () => setSelected(new Set()) },
    );
  }

  return (
    <Layout>
      <div className="flex flex-col gap-8">
        <PageHeading
          eyebrow="Admin"
          title="Events"
          subtitle="Organizer applications, grouped by where they'd run. Pick one point of contact per group."
        />

        <SlackHealthPanel health={slack_health} />

        <CustomPagesPanel pages={custom_pages} />

        <Panel
          title={`where-they-are.map - ${map_pins.length} awaiting review`}
          actions={
            slack_health.unplaced > 0 && slack_health.geocoder_available ? (
              <Button
                size="sm"
                onClick={() => router.post("/admin/events/geocode")}
              >
                Place {slack_health.unplaced} more
              </Button>
            ) : undefined
          }
        >
          {map_pins.length === 0 ? (
            <EmptyState
              title="No cities placed yet"
              description={
                slack_health.geocoder_available
                  ? "Cities are resolved in the background as applications come in."
                  : "Set ANTHROPIC_API_KEY to resolve the cities organizers typed into map pins."
              }
            />
          ) : (
            <div className="flex flex-col gap-3">
              <EventMap pins={map_pins} />

              {slack_health.geocode_failures.length > 0 && (
                <div className="border-line bg-surface-alt border-2 p-3">
                  <p className="font-display text-caption text-ink font-bold">
                    {slack_health.geocode_failures.length} couldn&rsquo;t be
                    placed
                  </p>
                  {slack_health.geocode_failures.map((failure) => (
                    <p key={failure.id} className="text-caption text-ink-muted">
                      {failure.location_label} - {failure.error}
                    </p>
                  ))}
                </div>
              )}
            </div>
          )}
        </Panel>

        <SuggestionPanel suggestions={suggestions} onApply={applyGroupTo} />

        <section className="flex flex-col gap-4">
          <h2 className="font-display text-heading text-ink font-bold">
            Awaiting review ({waiting})
            {contested > 0 && (
              <span className="text-ink-muted font-normal">
                {" "}
                · {contested} contested
              </span>
            )}
          </h2>

          {groups.length === 0 ? (
            <EmptyState
              title="Nothing to review"
              description="New applications land here as organizers submit them."
            />
          ) : (
            groups.map((group) => (
              <GroupPanel
                key={group.key}
                group={group}
                selected={selected}
                onToggle={toggle}
                onToggleAll={toggleAll}
                flagLabels={application_flag_labels}
              />
            ))
          )}

          {selected.size > 0 && (
            <BulkGroupBar
              count={selected.size}
              labels={existingLabels}
              onApply={applyGroup}
              onClear={() => setSelected(new Set())}
            />
          )}
        </section>

        {approved.length > 0 && (
          <Panel title={`approved.csv - ${approved.length}`}>
            {approved.map((event) => (
              <EventRow key={event.id} event={event} />
            ))}
          </Panel>
        )}

        {rejected.length > 0 && (
          <Panel title={`sent-back.csv - ${rejected.length}`} tone="alt">
            {rejected.map((event) => (
              <EventRow key={event.id} event={event} />
            ))}
          </Panel>
        )}

        {not_selected.length > 0 && (
          <Panel title={`not-selected.csv - ${not_selected.length}`} tone="alt">
            {not_selected.map((event) => (
              <EventRow key={event.id} event={event} />
            ))}
          </Panel>
        )}
      </div>
    </Layout>
  );
}
